import Popper, { HintPlacement } from '@zvuk-b2b/react-uikit/ui/Popper';
import VideoMediafilePoster from 'application/components/VideoMediafileItemPoster';
import { useDetectTextOverflow } from 'application/hooks/useDetectTextOverflow';
import React, { useState } from 'react';

import IconButton from '@zvuk-b2b/react-uikit/ui/IconButton';
import Input, { InputSize, InputType } from '@zvuk-b2b/react-uikit/ui/Input';
import TimeInput from '@zvuk-b2b/react-uikit/ui/TimeInput';
import IconCopy from '@zvuk-b2b/react-uikit/ui/icons/Copy';
import IconTrash2 from '@zvuk-b2b/react-uikit/ui/icons/Trash2';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import { TEXTS } from '../../texts';
import styles from './index.module.scss';
import { defaultTimeInputWithSecondsValidator, formatMillisecondsToTime, parseTimeString } from './utils';
import { DEFAULT_IMAGE_DURATION } from '../../consts';
import { getRepeatsCount } from '../../utils';

interface ItemContentProps {
  title?: string | null;
  duration?: number | null;
  imgLink?: string | null;
  extension?: string | null;
  playDuration?: number | null;
  onSetDuration: (id: string, duration: number) => void;
  id?: string | null;
  onRemoveMediafile: (id: string) => void;
  onCopyMediafile: (id: string) => void;
}

export const TIME_PATTERN = /^(?:\d|[01]\d|2[0-3]):(?:\d|[01]\d|2[0-3]):[0-5]\d$/;

const ItemContent: React.FC<ItemContentProps> = ({
  title,
  duration,
  imgLink,
  extension,
  playDuration,
  onSetDuration,
  id,
  onRemoveMediafile,
  onCopyMediafile,
}) => {
  const { isOverflow, textRef } = useDetectTextOverflow('[data-testid="video_media_title"]');

  const { isMobile } = useMedia();

  const videoHelpText = isMobile ? TEXTS.VIDEO_MOBILE_HELP_TEXT : TEXTS.VIDEO_HELP_TEXT;
  const imageHelpText = isMobile ? TEXTS.IMAGE_MOBILE_HELP_TEXT : TEXTS.IMAGE_HELP_TEXT;

  const repeatCount = getRepeatsCount(duration, playDuration);

  const descriptionText = duration ? videoHelpText : imageHelpText;

  const [isValid, setIsValid] = useState(true);

  const onCopyClick = () => {
    if (id) {
      onCopyMediafile(id);
    }
  };

  const onRemoveClick = () => {
    if (id) {
      onRemoveMediafile(id);
    }
  };

  return (
    <div className={styles.VideoMediafilePreviewItemContent}>
      <div className={styles.VideoMediafilePreviewItemContentPosterContainer}>
        <VideoMediafilePoster
          title={title}
          link={imgLink}
          duration={duration || playDuration || DEFAULT_IMAGE_DURATION}
          extension={extension}
        />
      </div>

      <div
        ref={textRef}
        className={styles.VideoMediafilePreviewItemContentTextContainer}>
        <Popper
          placement={HintPlacement.PLACEMENT_TOP_START}
          containerClassName={styles.popper}
          isShow={isOverflow}
          offset={[10, 10]}
          triggerNode={
            <span
              data-testid="video_media_title"
              className={styles.VideoMediafilePreviewItemContentTextContainerTitle}>
              {title}
            </span>
          }>
          {title}
        </Popper>
        {!duration && (
          <TimeInput
            elementId="image_duration_input"
            value={formatMillisecondsToTime(playDuration)}
            inputValidator={defaultTimeInputWithSecondsValidator}
            type={InputType.TEXT}
            forceError={!isValid}
            onBlur={(durationString) => {
              if (id) {
                const newPlayDuration = parseTimeString(durationString.target.value).milliseconds;
                setIsValid(Number(newPlayDuration) > 0);
                onSetDuration(id, newPlayDuration);
              }
            }}
            onChange={(durationString) => {
              const newPlayDuration = parseTimeString(durationString).milliseconds;
              setIsValid(Number(newPlayDuration) > 0);
            }}
            defaultValue={formatMillisecondsToTime(playDuration)}
            pattern={TIME_PATTERN}
            mask={TEXTS.TIME_MASK}
            placeholder={TEXTS.TIME_PLACEHOLDER}
            maxLength={8}
          />
        )}
        {!!duration && (
          <Input
            value={String(repeatCount)}
            size={InputSize.MEDIUM}
            type={InputType.NUMBER}
            maxLength={3}
            min={1}
            autoComplete="off"
            elementId="video_mediafiles_count"
            placeholder="1"
            forceError={!isValid}
            onChange={(durationString) => {
              const newPlayDuration = Number(durationString) * duration;
              setIsValid(Number(newPlayDuration) > 0);
            }}
            onBlur={(durationString) => {
              if (id && duration) {
                const newPlayDuration = Number(durationString.target.value) * duration;
                setIsValid(Number(newPlayDuration) > 0);
                onSetDuration(id, newPlayDuration);
              }
            }}
          />
        )}
        <div className={`${styles.description} ${!isValid && styles.error}`}>{descriptionText}</div>
      </div>
      <div className={styles.buttons}>
        <IconButton
          onClick={onCopyClick}
          hint={TEXTS.DUPLICATE}
          className={styles.actionButton}
          elementId="copy_video_mediafile_button">
          <IconCopy className={styles.icon} />
        </IconButton>
        <IconButton
          className={styles.actionButton}
          hint={TEXTS.REMOVE_FROM_VIDEO_STREAM}
          elementId="remove_video_mediafile_button"
          onClick={onRemoveClick}>
          <IconTrash2 className={styles.icon} />
        </IconButton>
      </div>
    </div>
  );
};

export default ItemContent;
